/**
 * THE NOTES CARD — one card, wherever the record is.
 *
 * Anthony: "need to add notes to the leads, contact cards across the
 * different places where the six tile lives. Which even a lead should have
 * this so information could be added. put add note under history, allow
 * for notes to be added or edited and record the date, time and who added
 * note. Can add photo to note as well please"
 *
 * ONE COMPONENT RATHER THAN THREE. A note on a lead, a note on a customer
 * and a note on a job are the same act — somebody writing down what they
 * were told — and building it three times is how they end up looking and
 * behaving differently on the three screens a person moves between all
 * day. This mounts into a host div and takes the record it is about.
 *
 * IT SAYS WHEN, TO THE MINUTE. Everything else on these screens reads
 * "two hours ago", which is right for glancing at a list and useless for
 * a note somebody may be asked to account for. Both are shown: the exact
 * stamp is the record, the relative one is the reading.
 */

import { esc } from "../ui/dom.ts";
import type { Role } from "../domain/permissions.ts";
import {
  listNotes, addNote, editNote, deleteNote, addNotePhoto, noteImageSrc,
  notesNotBuiltYet, whoAmI, type Note, type NoteTarget,
} from "../data/notes.ts";
import { mates, whoIsTagged, tellThem, type Mate } from "../data/mentions.ts";
import { shopWords } from "../ui/words.ts";
import { shopZone } from "../ui/shopclock.ts";

/**
 * THE TIME, SAID ONCE.
 *
 * This read "Sep 8, 7:04 PM · 2 hours ago", which is the same fact twice
 * joined by a middle dot, and a person reading a note does not need to be
 * told the time in two units. A note from today wants the clock, because
 * the question is what happened this morning. A note from last week wants
 * the date, because the clock means nothing without it. The full stamp
 * stays on the element's title for anybody who needs to be precise.
 *
 * The shop's clock, not the browser's — the same one the schedule uses.
 * Read when a note is drawn, not at import, because the zone is primed
 * from the sign-in after this module loads.
 */
const dayIn = (d: Date): string =>
  d.toLocaleDateString("en-CA", { timeZone: shopZone() });

const WHEN = (iso: string): string => {
  const d = new Date(iso);
  const clock = d.toLocaleTimeString("en-US",
    { timeZone: shopZone(), hour: "numeric", minute: "2-digit" });
  const today = dayIn(new Date());
  const then = dayIn(d);
  if (then === today) return `${clock} today`;

  const y = new Date(Date.now() - 864e5);
  if (then === dayIn(y)) return `${clock} yesterday`;

  return d.toLocaleDateString("en-US",
    { timeZone: shopZone(), month: "short", day: "numeric" }) + `, ${clock}`;
};

const MAY_WRITE: Role[] = ["admin", "supervisor", "advisor", "technician"];

/**
 * Photographs load their URLs after the card is drawn, because a public
 * URL is a round trip per image and a card that waits for eight of them
 * before showing any of the words is a card that looks broken.
 */
async function paintPhotos(root: HTMLElement, notes: Note[]): Promise<void> {
  for (const n of notes) {
    for (const p of n.photos ?? []) {
      const img = root.querySelector<HTMLImageElement>(`img[data-photo="${p.id}"]`);
      if (!img) continue;
      const src = await noteImageSrc(p);
      if (src) img.src = src;
      else img.alt = "This photo would not load. Refresh to try again.";
    }
  }
}

function noteHtml(n: Note, all: Mate[] = []): string {
  const shots = (n.photos ?? []).map((p) => `
    <a class="noteshot" href="#" data-open="${esc(p.id)}">
      <img data-photo="${esc(p.id)}" alt="Photo on this note" loading="lazy"></a>`).join("");

  return `<li class="note" data-note="${esc(n.id)}">
    <div class="note-w" title="${esc(new Date(n.created_at).toString())}">
      <b>${esc(n.author_name)}</b><span class="when">${esc(WHEN(n.created_at))}</span>
    </div>
    <div class="note-b">${withTags(esc(n.body), all)}</div>
    ${n.edited_at ? `<div class="note-ed">Edited by ${
      esc(n.edited_name ?? "somebody")}, ${esc(WHEN(n.edited_at))}</div>` : ""}
    ${shots ? `<div class="noteshots">${shots}</div>` : ""}
    <!-- Two things you do to a note, and one you rarely should. Delete
         sits apart and stays quiet until it is being used; it is not a
         peer of Edit and should not read as one. -->
    <div class="note-acts">
      <button class="navlink" data-edit="${esc(n.id)}">Edit</button>
      <button class="navlink" data-shot="${esc(n.id)}">Add a photo</button>
      <button class="navlink quiet" data-del="${esc(n.id)}">Delete</button>
    </div>
  </li>`;
}

/**
 * Draw the card into `hostId` and wire it.
 *
 * `title` differs by screen only because "Notes" on a lead sheet next to
 * the old single Notes field would read as the same thing twice.
 */
/**
 * @SOMEBODY, WRITTEN OUT.
 *
 * The handle stays in the text because that is what was typed and what
 * reads back correctly in a year. This only makes it look like what it is.
 * Escaped first, then the handles are wrapped -- never the other way
 * round, or a customer called <script> gets to write markup.
 */
function withTags(escaped: string, all: Mate[]): string {
  if (!all.length) return escaped;
  const byLength = [...all].sort((a, b) => b.handle.length - a.handle.length);
  let out = escaped;
  for (const m of byLength) {
    const re = new RegExp(`(^|[^\\w@])@(${m.handle})(?![\\w])`, "gi");
    out = out.replace(re, `$1<b class="notetag" title="${esc(m.name)}">@$2</b>`);
  }
  return out;
}

/**
 * WHERE THE BELL SENDS THEM. A note lives on a record, and the alert has
 * to open THAT record -- landing on a notes list with no idea which car it
 * was about is the alert wasting the trip it exists to save.
 */
function hrefFor(t: NoteTarget): string {
  if (t.jobId) return `#/job/${t.jobId}`;
  if (t.customerId) return `#/customer/${t.customerId}`;
  if (t.enquiryId) return `#/leads`;
  return "#/";
}

/** What the alert calls the thing, in the words of the screen it came from. */
function aboutFor(t: NoteTarget, title?: string): string {
  if (t.jobId) return "a job";
  if (t.customerId) return "a customer";
  if (t.enquiryId) return "a lead";
  if (t.vehicleId) return `a ${shopWords().thing}`;
  return (title ?? "a note").toLowerCase();
}

export async function notesCard(
  hostId: string, target: NoteTarget, role: Role = "viewer",
  /**
   * `show` widens what is LISTED without widening what a new note is
   * STAMPED WITH, and the two really are different questions. A lead that
   * became a job shows the notes from both, but a note typed on the lead
   * sheet is a note about the lead — stamping it with the job as well
   * would make it reappear under a car it was never about.
   */
  opts: { title?: string; blurb?: string; show?: NoteTarget } = {},
): Promise<void> {
  const host = document.getElementById(hostId);
  if (!host) return;
  const mayWrite = MAY_WRITE.includes(role);

  /* Everybody who can be tagged, and me. Both are cached in their own
     modules, so this is one read per session rather than per draw. */
  let crew: Mate[] = [];
  let mine: { id: string | null; name: string; accountId: string } | null = null;

  const draw = async (): Promise<void> => {
    let notes: Note[] = [];
    if (mayWrite) {
      [crew, mine] = await Promise.all([
        mates().catch(() => [] as Mate[]),
        whoAmI().catch(() => null),
      ]);
    }
    try {
      notes = await listNotes({ ...target, ...(opts.show ?? {}) });
    } catch (e) {
      // The table is not there yet: hide the card rather than put a
      // database error in front of a service advisor. See notesNotBuiltYet.
      if (notesNotBuiltYet(e)) { host.innerHTML = ""; host.hidden = true; return; }
      host.innerHTML = `<div class="card-head">${esc(opts.title ?? "Notes")}</div>
        <div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
      return;
    }

    host.innerHTML = `
      <div class="card-head">${esc(opts.title ?? "Notes")} &middot; ${notes.length}</div>
      ${mayWrite ? `
        <div class="noteadd">
          <label class="field"><span>Note</span><textarea id="${hostId}-body" rows="2" maxlength="4000"></textarea></label>
          <!-- THE PICKER. Type @ and it lists the shop. It is not a search
               box you open; it appears while you are already typing the
               thing it is about, which is the only way a tag gets used on
               a phone in a bay. -->
          <div class="tagpick" id="${hostId}-pick-who" hidden></div>
          <div class="noteadd-acts">
            <button class="btn primary" id="${hostId}-save">Add note</button>
            <span class="fine" id="${hostId}-msg"></span>
          </div>
          ${crew.length > 1 ? `<div class="notehint">Type <b>@</b> to tag
            somebody &mdash; it puts it in their bell.</div>` : ""}
        </div>` : ""}
      ${notes.length
        ? `<ul class="notelist">${notes.map((n) => noteHtml(n, crew)).join("")}</ul>`
        /* Somebody looking at a box they can type into does not need to be
           told the list is empty. Only a viewer, who cannot write one, is
           owed the sentence. */
        : mayWrite ? "" : `<div class="empty">No notes on this yet.</div>`}
      <!-- Two inputs, because capture="environment" on a single one hides
           the gallery entirely on an iPhone. The same pair the photo tile
           uses. -->
      <input type="file" id="${hostId}-cam" accept="image/*" capture="environment" hidden>
      <input type="file" id="${hostId}-pick" accept="image/*" multiple hidden>`;

    void paintPhotos(host, notes);

    const msg = (text: string, bad = false): void => {
      const m = document.getElementById(`${hostId}-msg`);
      if (m) m.innerHTML = text ? `<span class="msg${bad ? " bad" : ""}">${esc(text)}</span>` : "";
    };

    /* ── writing one ─────────────────────────────────────────────── */
    const box = document.getElementById(`${hostId}-body`) as HTMLTextAreaElement | null;
    document.getElementById(`${hostId}-save`)?.addEventListener("click", async () => {
      const body = (box?.value ?? "").trim();
      if (!body) { msg("Type the note first."); box?.focus(); return; }
      const b = document.getElementById(`${hostId}-save`) as HTMLButtonElement;
      // The button said "Add note" and then "Saving…", which is two names
      // for one action. It keeps its verb.
      b.disabled = true; b.textContent = "Adding…";
      /* Worked out from the finished text rather than remembered as the
         picker was used, so deleting an @name by hand un-tags them. */
      const tagged = whoIsTagged(body, crew);
      try {
        const made = await addNote(target, body, tagged.map((m) => m.id));
        if (box) box.value = "";

        /**
         * RINGING THEIR BELL IS A SEPARATE ACT FROM WRITING THE NOTE.
         *
         * The note is saved by the line above. If the alert cannot be
         * written -- an old deploy without migration 0089, a policy that
         * is not there yet -- the note still stands and the failure is
         * said out loud beside it. Silently swallowing it would leave
         * somebody believing they had asked a colleague something.
         */
        let rang = 0;
        let failed = "";
        if (tagged.length && mine) {
          try {
            rang = await tellThem({
              noteId: made.id, who: tagged,
              authorId: mine.id, authorName: mine.name, accountId: mine.accountId,
              body, href: hrefFor(target), about: aboutFor(target, opts.title),
            });
          } catch (e) {
            failed = e instanceof Error ? e.message : String(e);
          }
        }
        await draw();
        if (failed) {
          msg(`Note saved, but the tag did not go: ${failed}`, true);
        } else if (rang) {
          const names = tagged.filter((m) => m.id !== mine?.id).map((m) => m.name);
          msg(`Saved, and it is in ${names.join(" and ")}'s bell.`);
        }
      } catch (e) {
        b.disabled = false; b.textContent = "Add note";
        msg(e instanceof Error ? e.message : String(e), true);
      }
    });

    /**
     * ─── THE @ PICKER ───────────────────────────────────────────
     *
     * It watches the word being typed rather than opening on a button.
     * Somebody in a bay with one hand free is already typing the note;
     * making them stop, find a control and pick a person is how a feature
     * gets used twice and then never again.
     *
     * Arrow keys and Enter, because on a laptop the whole point is not
     * lifting your hands. Escape closes it and leaves the text alone --
     * an @ that was meant literally stays literal.
     */
    const picker = document.getElementById(`${hostId}-pick-who`);
    let hits: Mate[] = [];
    let at = -1;

    const shut = (): void => {
      if (picker) { picker.hidden = true; picker.innerHTML = ""; }
      hits = []; at = -1;
    };

    /** The @word the caret is sitting in, or null. */
    const wordAtCaret = (): { term: string; from: number } | null => {
      if (!box) return null;
      const upto = box.value.slice(0, box.selectionStart ?? 0);
      const m = /(?:^|[^\w@])@([a-z]*)$/i.exec(upto);
      if (!m) return null;
      return { term: (m[1] ?? "").toLowerCase(), from: upto.length - (m[1] ?? "").length - 1 };
    };

    const paint = (): void => {
      if (!picker) return;
      picker.innerHTML = hits.map((m, i) => `
        <button type="button" class="tagopt${i === at ? " on" : ""}" data-mate="${esc(m.id)}">
          <b>${esc(m.name)}</b><i>@${esc(m.handle)}</i>
          <span class="tagrole">${esc(m.role)}</span>
        </button>`).join("");
      picker.hidden = false;
      picker.querySelectorAll<HTMLButtonElement>("[data-mate]").forEach((btn, i) =>
        btn.addEventListener("mousedown", (ev) => { ev.preventDefault(); choose(i); }));
    };

    /** Put the handle in, with a space after it, and carry on typing. */
    const choose = (i: number): void => {
      const m = hits[i];
      const w = wordAtCaret();
      if (!m || !w || !box) return shut();
      const before = box.value.slice(0, w.from);
      const after = box.value.slice(box.selectionStart ?? 0);
      box.value = `${before}@${m.handle} ${after}`;
      const caret = before.length + m.handle.length + 2;
      box.focus();
      box.setSelectionRange(caret, caret);
      shut();
    };

    const look = (): void => {
      if (!crew.length) return shut();
      const w = wordAtCaret();
      if (!w) return shut();
      hits = crew
        .filter((m) => m.id !== mine?.id)
        .filter((m) => !w.term
          || m.handle.startsWith(w.term)
          || m.name.toLowerCase().includes(w.term))
        .slice(0, 6);
      if (!hits.length) return shut();
      at = 0;
      paint();
    };

    box?.addEventListener("input", look);
    box?.addEventListener("blur", () => setTimeout(shut, 120));
    box?.addEventListener("keydown", (e) => {
      if (picker?.hidden !== false || !hits.length) return;
      if (e.key === "ArrowDown") { e.preventDefault(); at = (at + 1) % hits.length; paint(); }
      else if (e.key === "ArrowUp") { e.preventDefault(); at = (at - 1 + hits.length) % hits.length; paint(); }
      else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); choose(at); }
      else if (e.key === "Escape") { e.preventDefault(); shut(); }
    });

    /* ── editing one ─────────────────────────────────────────────── */
    host.querySelectorAll<HTMLButtonElement>("[data-edit]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset["edit"] ?? "";
        const li = host.querySelector<HTMLElement>(`li[data-note="${id}"]`);
        const note = notes.find((n) => n.id === id);
        if (!li || !note || li.querySelector("textarea")) return;

        const body = li.querySelector(".note-b");
        if (!body) return;
        body.innerHTML = `<textarea class="noteedit" rows="3" maxlength="4000">${
          esc(note.body)}</textarea>
          <div class="noteadd-acts">
            <button class="btn sm primary" data-keep="${esc(id)}">Save</button>
            <button class="btn sm" data-drop="${esc(id)}">Cancel</button>
            <span class="fine" data-emsg="${esc(id)}"></span>
          </div>`;

        li.querySelector<HTMLButtonElement>(`[data-drop="${id}"]`)
          ?.addEventListener("click", () => void draw());

        li.querySelector<HTMLButtonElement>(`[data-keep="${id}"]`)
          ?.addEventListener("click", async (ev) => {
            const b = ev.currentTarget as HTMLButtonElement;
            const next = li.querySelector<HTMLTextAreaElement>("textarea")?.value ?? "";
            b.disabled = true; b.textContent = "Saving…";
            try {
              await editNote(id, next);
              await draw();
            } catch (e) {
              b.disabled = false; b.textContent = "Save";
              const m = li.querySelector(`[data-emsg="${id}"]`);
              if (m) m.innerHTML = `<span class="msg bad">${
                esc(e instanceof Error ? e.message : String(e))}</span>`;
            }
          });
      });
    });

    /* ── deleting one ────────────────────────────────────────────── */
    host.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const id = btn.dataset["del"] ?? "";
        /* Two taps rather than a modal: a note is somebody's record of a
           conversation and deleting one by mis-tap on a phone is easy. */
        if (btn.dataset["sure"] !== "1") {
          btn.dataset["sure"] = "1"; btn.textContent = "Sure?";
          setTimeout(() => {
            if (btn.dataset["sure"] === "1") { btn.dataset["sure"] = ""; btn.textContent = "Delete"; }
          }, 4000);
          return;
        }
        btn.disabled = true; btn.textContent = "Deleting…";
        try { await deleteNote(id); await draw(); }
        catch (e) { btn.disabled = false; btn.textContent = "Delete";
          msg(e instanceof Error ? e.message : String(e), true); }
      });
    });

    /* ── a photo onto one ────────────────────────────────────────── */
    const cam = document.getElementById(`${hostId}-cam`) as HTMLInputElement | null;
    const pick = document.getElementById(`${hostId}-pick`) as HTMLInputElement | null;
    let onto: Note | null = null;

    host.querySelectorAll<HTMLButtonElement>("[data-shot]").forEach((btn) => {
      btn.addEventListener("click", () => {
        onto = notes.find((n) => n.id === btn.dataset["shot"]) ?? null;
        /* The camera on a phone, the file picker on a desktop. One button
           rather than two here because the note already has its own row of
           actions and a third would crowd it; the plain picker is the
           fallback and takes gallery photos on both. */
        (/iPhone|iPad|Android/i.test(navigator.userAgent) ? cam : pick)?.click();
      });
    });

    const absorb = async (input: HTMLInputElement): Promise<void> => {
      const files = Array.from(input.files ?? []);
      const note = onto;
      input.value = "";
      if (!files.length || !note) return;
      msg(`Sending ${files.length === 1 ? "the photo" : `${files.length} photos`}…`);
      try {
        // One at a time: parallel uploads on a car park signal is how you
        // get timeouts instead of photos.
        for (const f of files) await addNotePhoto(note, f);
        await draw();
      } catch (e) {
        msg(e instanceof Error ? e.message : String(e), true);
      }
    };
    cam?.addEventListener("change", () => void absorb(cam));
    pick?.addEventListener("change", () => void absorb(pick));

    /* Opening a photo is the browser's own job — a new tab shows it full
       size, zoomable, and saveable, which is what somebody tapping a
       thumbnail wants. */
    host.querySelectorAll<HTMLAnchorElement>("[data-open]").forEach((a) => {
      a.addEventListener("click", (ev) => {
        ev.preventDefault();
        const img = a.querySelector("img");
        if (img?.src) window.open(img.src, "_blank", "noopener");
      });
    });
  };

  host.innerHTML = `<div class="loading">Reading&hellip;</div>`;
  await draw();
}
